import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMyOrgs } from '../api/organizations';
import { orgExchange } from '../api/auth';
import { useAuth } from './auth';
import { setActiveOrgId } from './orgState';

const OrgContext = createContext(null);

const ORG_KEY = 'cap.activeOrgId';

function readPersistedOrgId() {
  try {
    return localStorage.getItem(ORG_KEY) || null;
  } catch {
    return null;
  }
}

function persistOrgId(orgId) {
  try {
    if (orgId) {
      localStorage.setItem(ORG_KEY, orgId);
    } else {
      localStorage.removeItem(ORG_KEY);
    }
  } catch {
    // localStorage unavailable
  }
}

export function OrgProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchOrgs = useCallback(async () => {
    if (!isAuthenticated) {
      setOrgs([]);
      setActiveOrg(null);
      setActiveOrgId(null);
      return;
    }

    setLoading(true);
    try {
      const data = await getMyOrgs();
      const orgList = (data.organizations || []).map(item => {
        if (item.organization) {
          return {
            ...item.organization,
            role: item.membership?.role,
            membership: item.membership,
            canInvite: item.canInvite === true,
          };
        }
        return item;
      });
      setOrgs(orgList);

      const persisted = readPersistedOrgId();
      const match = orgList.find(o => o.orgId === persisted);
      const selected = match || orgList[0] || null;

      setActiveOrg(selected);
      setActiveOrgId(selected?.orgId || null);
      persistOrgId(selected?.orgId || null);
    } catch {
      setOrgs([]);
      setActiveOrg(null);
      setActiveOrgId(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const { adoptTokens } = useAuth();

  const switchOrg = useCallback(
    async orgId => {
      const match = orgs.find(o => o.orgId === orgId);
      if (!match) return;
      setActiveOrg(match);
      setActiveOrgId(match.orgId);
      persistOrgId(match.orgId);
      try {
        const data = await orgExchange(orgId);
        if (data.accessToken) adoptTokens(data);
      } catch {
        // Best-effort: the X-Org-Id header still scopes requests even if
        // the token exchange fails, so the UI remains functional.
      }
    },
    [orgs, adoptTokens]
  );

  const value = useMemo(
    () => ({
      orgs,
      activeOrg,
      loading,
      switchOrg,
      refresh: fetchOrgs,
    }),
    [orgs, activeOrg, loading, switchOrg, fetchOrgs]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside OrgProvider');
  return ctx;
}
